import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supportChat, createSupportTicket } from '../utils/api';
import { MessageCircle, X, Send, LifeBuoy, CheckCircle2 } from 'lucide-react';

export const SupportChatWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm the GritClicks assistant. Ask me anything — setting up tracking, what a number means, how billing works. If I can't help, I'll open a ticket for a human." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [ticketMode, setTicketMode] = useState(false);
  const [ticketText, setTicketText] = useState('');
  const [ticketDone, setTicketDone] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, ticketMode, ticketDone]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setBusy(true);
    try {
      const { data } = await supportChat({ message: text, session_id: sessionId, page: location.pathname });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply, escalate: data.escalate }]);
      if (data.escalate) {
        setTicketMode(true);
        setTicketText(text);
      }
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: "Something went wrong on my end — use the 'Talk to a human' button below and we'll get you a ticket." }]);
    } finally {
      setBusy(false);
    }
  };

  const submitTicket = async () => {
    if (!ticketText.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await createSupportTicket({ question: ticketText, session_id: sessionId, page: location.pathname });
      setTicketDone(data.ticket_number);
      setTicketMode(false);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Ticket submission failed — please try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="support-widget-open-btn"
        className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-lime text-black flex items-center justify-center shadow-lg shadow-lime/20 hover:scale-105 transition-transform"
        title="Help & Support"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[540px] bg-[#14304a] border border-divider rounded-2xl shadow-2xl flex flex-col" data-testid="support-widget-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-divider">
        <div className="flex items-center gap-2">
          <LifeBuoy size={18} className="text-lime" />
          <p className="text-white font-bold text-sm">GritClicks Help & Support</p>
        </div>
        <button onClick={() => setOpen(false)} data-testid="support-widget-close-btn" className="text-gray hover:text-white"><X size={18} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
            m.role === 'user' ? 'ml-auto bg-lime text-black' : 'bg-slate-blue text-light-gray'
          }`} data-testid={`support-msg-${i}`}>
            {m.content}
          </div>
        ))}
        {busy && !ticketMode && <div className="bg-slate-blue text-muted text-sm rounded-xl px-3.5 py-2.5 w-16">…</div>}

        {ticketMode && !ticketDone && (
          <div className="bg-black border border-orange/40 rounded-xl p-3.5" data-testid="support-ticket-form">
            <p className="text-sm text-white font-bold mb-2">Open a support ticket</p>
            <textarea
              value={ticketText}
              onChange={(e) => setTicketText(e.target.value)}
              rows={3}
              data-testid="support-ticket-textarea"
              className="w-full px-3 py-2 bg-slate-blue border border-divider rounded-lg text-white text-sm focus:outline-none focus:border-orange resize-none mb-2"
              placeholder="Describe your question or issue…"
            />
            <button onClick={submitTicket} disabled={busy} data-testid="support-ticket-submit-btn"
              className="w-full py-2 rounded-lg bg-orange text-black font-bold text-sm hover:bg-orange/90 disabled:opacity-50">
              {busy ? 'Submitting…' : 'Submit Ticket'}
            </button>
          </div>
        )}

        {ticketDone && (
          <div className="bg-black border border-lime/40 rounded-xl p-3.5 text-center" data-testid="support-ticket-confirmation">
            <CheckCircle2 size={22} className="text-lime mx-auto mb-1" />
            <p className="text-white font-bold text-sm">Ticket <span className="text-lime" data-testid="support-ticket-number">{ticketDone}</span> created</p>
            <p className="text-xs text-light-gray mt-1">Someone will follow up by email. Keep that ticket number for reference.</p>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-divider">
        <div className="flex gap-2 mb-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Ask a question…"
            data-testid="support-chat-input"
            className="flex-1 px-3.5 py-2.5 bg-slate-blue border border-divider rounded-lg text-white text-sm focus:outline-none focus:border-lime"
          />
          <button onClick={send} disabled={busy || !input.trim()} data-testid="support-chat-send-btn"
            className="px-3.5 rounded-lg bg-lime text-black disabled:opacity-40"><Send size={16} /></button>
        </div>
        {!ticketMode && !ticketDone && (
          <button onClick={() => { setTicketMode(true); setTicketText(''); }} data-testid="support-talk-to-human-btn"
            className="text-xs text-muted hover:text-orange transition-colors">
            Need a person? Talk to a human →
          </button>
        )}
      </div>
    </div>
  );
};
